import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronsLeftRight } from "lucide-react";
import { useT } from "@/i18n_extra3";

const CASES = [
  { before: "/assets/ba1-before.jpg", after: "/assets/ba1-after.jpg" },
  { before: "/assets/ba2-before.jpg", after: "/assets/ba2-after.jpg" },
];

export const BeforeAfter = () => {
  const t = useT();
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);
  const [caseIdx, setCaseIdx] = useState(0);

  const update = (clientX) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };

  const pick = (i) => {
    setCaseIdx(i);
    setPos(50);
  };

  const current = CASES[caseIdx];

  return (
    <section className="py-28 sm:py-32 bg-[#050505]" data-testid="before-after-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#D4AF37]">{t.beforeAfter.label}</span>
          </div>
          <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl leading-[1.05] text-[#F8F8F8]">{t.beforeAfter.title}</h2>
        </motion.div>

        <div className="flex flex-wrap gap-3 mb-8" data-testid="before-after-cases">
          {t.beforeAfter.cases.map((label, i) => (
            <button
              key={label}
              onClick={() => pick(i)}
              data-testid={`before-after-case-${i + 1}`}
              className={`px-5 py-2.5 rounded-full text-xs font-bold tracking-[0.15em] uppercase border transition-all duration-300 ${
                i === caseIdx
                  ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                  : "border-white/15 text-white/60 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <motion.div
          ref={ref}
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="relative h-[400px] sm:h-[540px] overflow-hidden rounded-3xl border border-white/10 cursor-ew-resize select-none touch-none shadow-2xl"
          onPointerDown={(e) => { dragging.current = true; ref.current.setPointerCapture(e.pointerId); update(e.clientX); }}
          onPointerMove={(e) => dragging.current && update(e.clientX)}
          onPointerUp={() => { dragging.current = false; }}
          onPointerCancel={() => { dragging.current = false; }}
          data-testid="before-after-slider"
        >
          <AnimatePresence mode="sync">
            <motion.div
              key={caseIdx}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45 }}
              className="absolute inset-0"
            >
              <img src={current.after} alt="After — new windshield" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
              <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
                <img src={current.before} alt="Before — cracked windshield" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
              </div>
            </motion.div>
          </AnimatePresence>

          <span className="absolute top-5 left-5 bg-black/55 backdrop-blur-sm text-white text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full">
            {t.beforeAfter.before}
          </span>
          <span className="absolute top-5 right-5 bg-[#D4AF37]/90 text-black text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full">
            {t.beforeAfter.after}
          </span>

          <div className="absolute inset-y-0" style={{ left: `${pos}%` }} data-testid="before-after-handle">
            <div className="absolute inset-y-0 -left-px w-0.5 bg-[#D4AF37]" />
            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-[#D4AF37] shadow-2xl flex items-center justify-center">
              <ChevronsLeftRight className="w-6 h-6 text-black" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
